import {
  Avatar,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import DeleteWikiPost from "@/pages/wiki/components/DeleteWikiPost";
import EditWikiPost from "@/pages/wiki/components/EditWikiPost";
import MultiUpload from "@/pages/wiki/components/MultiUpload";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoCourseYear } from "@/shared/model/edo-course-year.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoWikiPost } from "@/shared/model/edo-wiki-post.model";
import { EdoEducationalStage } from "@/shared/model/enumerations/edo-educational-stage.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { DeleteDialogType, EditDialogType } from "@/shared/util/dialog-utils";
import { ITEMS_PER_PAGE, ITEMS_PER_PAGE_OPTIONS } from "@/shared/util/pagination.constants";
import { getEntities as getCourseYears } from "@/shared/reducers/entities/edo-course-year.reducer";
import {
  SubjectState,
  getSubjectsWithWikiPosts,
} from "@/shared/reducers/entities/edo-subject.reducer";
import { getEntities as getWikiPosts } from "@/shared/reducers/entities/edo-wiki-post.reducer";
import { Plus, BookOpen, Download, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IPaginationBaseState, Translate, translate } from "react-jhipster";
import { z } from "zod";

const educationalStages = [EdoEducationalStage.SECONDARIA_II, EdoEducationalStage.SECONDARIA_I];

const subjectFilterSchema = z.object({ subjectFilter: z.string() });
type SubjectFilterFormValues = z.infer<typeof subjectFilterSchema>;

const WikiFilterContent = ({
  setSubjectFilter,
  edoSubjectList,
}: {
  setSubjectFilter: (v: number | null) => void;
  edoSubjectList: SubjectState["subjectsByWikiPosts"];
}) => {
  const { control } = useFormContext<SubjectFilterFormValues>();
  return (
    <Controller
      name="subjectFilter"
      control={control}
      render={({ field }) => (
        <Select
          value={field.value}
          onValueChange={(v) => {
            field.onChange(v);
            setSubjectFilter(v === "NESSUNO" ? null : parseInt(v, 10));
          }}
        >
          <SelectTrigger className="min-w-[200px]">
            <SelectValue placeholder="Filtro materia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NESSUNO" disabled>
              Nessuno
            </SelectItem>
            {edoSubjectList.entities.map((subject) => (
              <SelectItem key={subject.id} value={String(subject.id)}>
                {subject.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
};

export const Wiki = () => {
  const dispatch = useAppDispatch();
  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const edoPostWikiList = useAppSelector<EntityState<IEdoWikiPost>>((state) => state.edoWikiPost);
  const edoCourseYearList = useAppSelector<EntityState<IEdoCourseYear>>(
    (state) => state.edoCourseYear
  );
  const { subjectsByWikiPosts: edoSubjectList } = useAppSelector<SubjectState>(
    (state) => state.edoSubject
  );
  const [stageTab, setStageTab] = useState<EdoEducationalStage>(EdoEducationalStage.SECONDARIA_II);
  const [subjectFilter, setSubjectFilter] = useState<number | null>(null);
  const [editPostWikiDialog, setEditPostWikiDialog] =
    useState<EditDialogType<IEdoWikiPost>>({ open: false, entity: null });
  const [deletePostWikiDialog, setDeletePostWikiDialog] =
    useState<DeleteDialogType<IEdoWikiPost>>({ open: false, entity: null });
  const [multiUploadDialog, setMultiUploadDialog] = useState<{ open: boolean }>({ open: false });
  const [yearTab, setYearTab] = useState<number | null>(null);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "title",
    order: "ASC",
  });

  const handleChangeYearTab = (value: string) => setYearTab(parseInt(value, 10));

  const handlePagination = (page: number) =>
    setPaginationState({ ...paginationState, activePage: page + 1 });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const openFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (e: React.MouseEvent, file: IEdoFile) => {
    e.stopPropagation();
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  const getWikiPostList = () => {
    if (subjectFilter === null || yearTab === null) return;
    const params = new URLSearchParams();
    params.append("filter.educationalStage", "$eq:" + stageTab);
    params.append("filter.years.id", "$eq:" + yearTab);
    params.append("filter.subject.id", "$eq:" + String(subjectFilter));
    dispatch(
      getWikiPosts({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: params.toString(),
      })
    );
  };

  useEffect(() => {
    dispatch(getCourseYears({ page: 0, size: 1000, sort: "year:ASC" }));
    dispatch(getSubjectsWithWikiPosts());
  }, []);

  useEffect(() => {
    getWikiPostList();
  }, [
    stageTab,
    yearTab,
    subjectFilter,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (edoSubjectList.entities.length) {
      setSubjectFilter(edoSubjectList.entities[0].id ?? null);
    }
  }, [edoSubjectList.entities]);

  useEffect(() => {
    if (edoCourseYearList.entities.length) {
      setYearTab(edoCourseYearList.entities[0].id ?? null);
    }
  }, [edoCourseYearList.entities]);

  useEffect(() => {
    if (edoPostWikiList.updateSuccess) {
      getWikiPostList();
    }
  }, [edoPostWikiList.updateSuccess]);

  const isAdmin = hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_ADMIN]);

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        {/* Header toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
          <div className="flex flex-1 flex-wrap items-center gap-3 sm:flex-initial">
            <Form
              schema={subjectFilterSchema}
              defaultValues={{
                subjectFilter: subjectFilter != null ? String(subjectFilter) : "NESSUNO",
              }}
              onSubmit={() => {}}
              className="flex-1 sm:flex-initial"
            >
              <WikiFilterContent
                setSubjectFilter={setSubjectFilter}
                edoSubjectList={edoSubjectList}
              />
            </Form>
          </div>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
                  disabled={edoPostWikiList.loading}
                >
                  <Plus className="h-4 w-4" />
                  Aggiungi
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setEditPostWikiDialog({ open: true, entity: null })}
                >
                  <Plus className="h-4 w-4" />
                  Caricamento singolo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMultiUploadDialog({ open: true })}>
                  <BookOpen className="h-4 w-4" />
                  Caricamento multiplo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Educational stage pills */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-mockup-border)] px-4 py-3">
          {educationalStages.map((stage) => (
            <button
              key={stage}
              type="button"
              onClick={() => setStageTab(stage)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                stageTab === stage
                  ? "bg-primary text-white shadow-md"
                  : "border border-[var(--color-mockup-border)] bg-surface text-[var(--color-mockup-text-mid)] hover:border-primary/30 hover:bg-primary/5"
              }`}
            >
              {translate(`edocendoApp.EdoEducationalStage.${stage}`)}
            </button>
          ))}
        </div>

        {/* Year tabs */}
        <Tabs
          value={yearTab != null ? String(yearTab) : ""}
          onValueChange={handleChangeYearTab}
          className="w-full"
        >
          <div className="border-b border-[var(--color-mockup-border)] px-4 py-3">
            <TabsList className="h-auto gap-2 bg-transparent p-0">
              {edoCourseYearList.entities.map((courseYear) => (
                <TabsTrigger
                  key={courseYear.id}
                  value={String(courseYear.id)}
                  disabled={
                    [4, 5].includes(courseYear.year ?? 0) &&
                    stageTab === EdoEducationalStage.SECONDARIA_I
                  }
                  className="rounded-full border border-[var(--color-mockup-border)] bg-surface px-4 py-1.5 text-xs font-bold shadow-sm data-[state=active]:bg-[var(--color-mockup-sky)] data-[state=active]:text-white data-[state=active]:border-transparent"
                >
                  {courseYear.year} {translate("edocendoApp.edoVideostorePost.year")}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>

        {/* Content */}
        {edoCourseYearList.loading && edoCourseYearList.entities.length === 0 ? (
          <div className="flex justify-center py-12">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <>
            {edoPostWikiList.loading && (
              <div className="flex justify-center py-8">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
            {edoPostWikiList.entities?.length ? (
              <List className="divide-y divide-[var(--color-mockup-border)]">
                {edoPostWikiList.entities.map((wikiPost) => (
                  <ListItem
                    key={wikiPost.id}
                    className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5"
                  >
                    <button
                      type="button"
                      onClick={() => openFile(wikiPost.file)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <ListItemIcon>
                        <Avatar className="h-10 w-10 rounded-2xl bg-primary/10 text-primary">
                          <BookOpen className="h-5 w-5" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
                            {wikiPost.title}
                          </span>
                        }
                        secondary={
                          <span className="text-xs text-[var(--color-mockup-text-light)]">
                            {wikiPost.subject?.name}
                          </span>
                        }
                      />
                    </button>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => downloadFile(e, wikiPost.file)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-primary/10 hover:text-primary"
                        aria-label="Scarica"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditPostWikiDialog({ open: true, entity: wikiPost });
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-primary/10 hover:text-primary"
                            aria-label="Modifica"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletePostWikiDialog({ open: true, entity: wikiPost });
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-light)] transition-colors hover:bg-[var(--color-mockup-coral)]/10 hover:text-[var(--color-mockup-coral)]"
                            aria-label="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </ListItem>
                ))}
              </List>
            ) : (
              !edoPostWikiList.loading && (
                <div className="px-6 py-12 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                    <Translate contentKey="edocendoApp.edoWikiPost.home.notFound" />
                  </p>
                </div>
              )
            )}
            <Pagination
              count={edoPostWikiList.totalItems}
              page={paginationState.activePage - 1}
              rowsPerPage={paginationState.itemsPerPage}
              onPageChange={handlePagination}
              onRowsPerPageChange={handleRowsPerPage}
              rowsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Righe per pagina:"
            />
          </>
        )}
      </Card>

      <EditWikiPost
        wikiPost={editPostWikiDialog.entity}
        open={editPostWikiDialog.open}
        onClose={() => setEditPostWikiDialog({ open: false, entity: null })}
      />
      <DeleteWikiPost
        wikiPost={deletePostWikiDialog.entity}
        open={deletePostWikiDialog.open}
        onClose={() => setDeletePostWikiDialog({ open: false, entity: null })}
      />
      <MultiUpload
        open={multiUploadDialog.open}
        onClose={() => setMultiUploadDialog({ open: false })}
      />
    </>
  );
};

export default Wiki;
